/**
 * PartnerCategoryList Component
 *
 * CURRENT IMPLEMENTATION:
 * - File upload functionality is fully working with real GraphQL backend
 * - Images are uploaded to the server and stored in the media system
 * - Partner categories are created/updated WITH image association
 * - Images are automatically loaded from the backend and displayed in the UI
 * - Full media integration is now working with the GraphQL backend
 */

import {
  Button,
  Col,
  Image,
  Input,
  Modal,
  Row,
  Table,
  Typography,
  Upload,
  message,
  type UploadFile,
} from "antd";
import {
  useCreatePartnerCategoryMutation,
  useDeletePartnerCategoryMutation,
  useGetAllPartnerCategoriesQuery,
  useUpdatePartnerCategoryMutation,
  useUploadFileMutation,
  useDeleteMediaMutation,
} from "../graphql/generated/hooks";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import RobinPage from "../components/RobinPage";
import EditDeleteActions from "../components/EditDeleteActions";
import { PlusOutlined } from "@ant-design/icons";

const { Text } = Typography;

function PartnerCategoryList() {
  const { t } = useTranslation();
  const [newPartnerCategory, setNewPartnerCategory] = useState<{
    name: string;
    nameAr: string;
  }>({ name: "", nameAr: "" });
  const [addTouched, setAddTouched] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Image upload state
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [editFileList, setEditFileList] = useState<UploadFile[]>([]);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [editPartnerCategoryId, setEditPartnerCategoryId] = useState<number>();
  const [editPartnerCategoryValues, setEditPartnerCategoryValues] = useState<{
    name: string;
    nameAr: string;
  }>({ name: "", nameAr: "" });

  const {
    data: partnerCategoriesData,
    loading,
    refetch,
  } = useGetAllPartnerCategoriesQuery();
  const [addPartnerCategory, { loading: creating }] =
    useCreatePartnerCategoryMutation();
  const [deletePartnerCategory, { loading: deleting }] =
    useDeletePartnerCategoryMutation();
  const [updatePartnerCategory, { loading: updating }] =
    useUpdatePartnerCategoryMutation();
  const [uploadFile, { loading: uploading }] = useUploadFileMutation();
  const [deleteMedia, { loading: deletingMedia }] = useDeleteMediaMutation();

  const trimmedNewName = newPartnerCategory.name.trim();
  const trimmedNewNameAr = newPartnerCategory.nameAr.trim();
  const canAdd = Boolean(trimmedNewName && trimmedNewNameAr);
  const isUploadingNewImage = fileList.some((f) => f.status === "uploading");
  const isAddDisabled = !canAdd || creating || uploading || deletingMedia || isUploadingNewImage;

  // Convert file to base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Real upload function using GraphQL
  const handleCustomUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;

    try {
      onProgress({ percent: 0 });

      // Convert file to base64
      const base64String = await convertFileToBase64(file);
      onProgress({ percent: 50 });

      // Upload using GraphQL mutation
      const result = await uploadFile({
        variables: {
          base64File: base64String,
          originalName: file.name,
          folder: "partner-categories",
        },
      });

      onProgress({ percent: 100 });

      if (result.data?.uploadFile) {
        const mediaData = {
          id: result.data.uploadFile.id,
          url: result.data.uploadFile.url,
          originalName: result.data.uploadFile.originalName,
          fileName: result.data.uploadFile.fileName,
          mimeType: result.data.uploadFile.mimeType,
          size: result.data.uploadFile.size,
          type: "IMAGE",
          altText: result.data.uploadFile.altText,
          description: result.data.uploadFile.description,
        };

        onSuccess(
          {
            id: result.data.uploadFile.id,
            url: result.data.uploadFile.url,
            mediaData, // Store full media data for future use
          },
          file,
        );
      } else {
        onError(new Error("Upload failed: No data returned"));
      }
    } catch (error) {
      console.error("Upload error:", error);
      onError(error);
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  const dataSource = [
    {
      key: -1,
      name: (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Input
            value={newPartnerCategory.name}
            placeholder={t("label.name")}
            allowClear
            status={addTouched && !trimmedNewName ? "error" : undefined}
            onChange={(rec) => {
              if (!addTouched) setAddTouched(true);
              setNewPartnerCategory((prev) => ({
                ...prev,
                name: rec?.target?.value,
              }));
            }}
          />
          {addTouched && !trimmedNewName ? (
            <Text type="danger" style={{ fontSize: 12 }}>
              {t("message.error.field_required")}
            </Text>
          ) : null}
        </div>
      ),
      nameAr: (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Input
            value={newPartnerCategory.nameAr}
            placeholder={t("label.name_ar")}
            allowClear
            status={addTouched && !trimmedNewNameAr ? "error" : undefined}
            onChange={(rec) => {
              if (!addTouched) setAddTouched(true);
              setNewPartnerCategory((prev) => ({
                ...prev,
                nameAr: rec?.target?.value,
              }));
            }}
          />
          {addTouched && !trimmedNewNameAr ? (
            <Text type="danger" style={{ fontSize: 12 }}>
              {t("message.error.field_required")}
            </Text>
          ) : null}
        </div>
      ),
      image: (
        <Upload
          maxCount={1}
          listType="picture-card"
          customRequest={handleCustomUpload}
          onChange={({ fileList: newFileList }) => {
            if (newFileList[0]?.status === "done") {
              messageApi.success(
                `${newFileList[0].name} file uploaded successfully`,
              );
            } else if (newFileList[0]?.status === "error") {
              messageApi.error(`${newFileList[0].name} file upload failed.`);
            }
            setFileList(newFileList);
          }}
          fileList={fileList}
          onRemove={async (file) => {
            try {
              // If the file has been uploaded and has an ID, delete it from the server
              if (file.response?.id) {
                await deleteMedia({
                  variables: {
                    id: file.response.id,
                  },
                });
                messageApi.success(`${file.name} deleted successfully`);
              }
            } catch (error) {
              console.error("Delete error:", error);
              messageApi.error(`Failed to delete ${file.name}`);
            } finally {
              // Always clear the file list regardless of delete success/failure
              setFileList([]);
            }
          }}
        >
          {fileList.length ? null : uploadButton}
        </Upload>
      ),
      actions: (
        <Button
          type="primary"
          size="small"
          loading={creating}
          disabled={isAddDisabled}
          onClick={async () => {
            setAddTouched(true);
            if (!canAdd) return;

            await addPartnerCategory({
              variables: {
                name: trimmedNewName,
                nameAr: trimmedNewNameAr,
                media: fileList[0]?.response?.mediaData
                  ? {
                      fileName: fileList[0].response.mediaData.fileName,
                      originalName: fileList[0].response.mediaData.originalName,
                      mimeType: fileList[0].response.mediaData.mimeType,
                      size: fileList[0].response.mediaData.size,
                      type: fileList[0].response.mediaData.type,
                      url: fileList[0].response.mediaData.url,
                      isActive: true,
                      altText:
                        fileList[0].response.mediaData.altText || trimmedNewName,
                      description:
                        fileList[0].response.mediaData.description ||
                        `Image for ${trimmedNewName}`,
                    }
                  : undefined,
              },
            })
              .then(() => {
                messageApi.success(t("message.success.data_created"));
                refetch();
                setNewPartnerCategory({ name: "", nameAr: "" });
                setAddTouched(false);
                setFileList([]); // Reset file list after successful creation
              })
              .catch((error) => {
                messageApi.error(t("message.error.data_create_failed"));
                console.error(error);
              });
          }}
        >
          {t("button.add")}
        </Button>
      ),
    },
    ...(partnerCategoriesData?.getAllPartnerCategories?.map(
      (partnerCategory, index) => ({
        key: index,
        name: partnerCategory.name,
        nameAr: partnerCategory.nameAr,
        image: (
          <div
            style={{
              width: 100,
              height: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px dashed #d9d9d9",
              borderRadius: 8,
            }}
          >
            {partnerCategory.media ? (
              <Image
                src={partnerCategory.media.url}
                alt={partnerCategory.media.altText || partnerCategory.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 8,
                }}
                preview={{
                  mask: false,
                }}
              />
            ) : (
              <span style={{ color: "#999", fontSize: 12 }}>No Image</span>
            )}
          </div>
        ),
        actions: (
          <EditDeleteActions
            onEdit={() => {
              setEditPartnerCategoryId(partnerCategory.id);
              setEditPartnerCategoryValues({
                name: partnerCategory.name,
                nameAr: partnerCategory.nameAr,
              });
              setEditFileList([]);
              setRemoveExistingImage(false);
              refetch();
              setEditMode(true);
            }}
            onDelete={() => {
              deletePartnerCategory({ variables: { id: partnerCategory.id } })
                .then(() => {
                  messageApi.success(t("message.success.data_deleted"));
                  refetch();
                })
                .catch((error) => {
                  messageApi.error(t("message.error.data_delete_failed"));
                  console.error(error);
                });
            }}
            loadingDelete={deleting}
          />
        ),
      })
    ) ?? []),
  ];

  return (
    <RobinPage title={t("label.menu.partner_categories")}>
      <Row gutter={[10, 10]}>
        <Modal
          title="Edit Partner Category"
          open={editMode}
          onOk={() => {
            const currentCategory = partnerCategoriesData?.getAllPartnerCategories.find(
              (rec) => rec.id === editPartnerCategoryId
            );
            if (editPartnerCategoryId && currentCategory) {
              updatePartnerCategory({
                variables: {
                  id: editPartnerCategoryId,
                  name: editPartnerCategoryValues.name,
                  nameAr: editPartnerCategoryValues.nameAr,
                  media: editFileList[0]?.response?.mediaData ? {
                    fileName: editFileList[0].response.mediaData.fileName,
                    originalName: editFileList[0].response.mediaData.originalName,
                    mimeType: editFileList[0].response.mediaData.mimeType,
                    size: editFileList[0].response.mediaData.size,
                    type: editFileList[0].response.mediaData.type,
                    url: editFileList[0].response.mediaData.url,
                    isActive: true,
                    altText: editFileList[0].response.mediaData.altText || currentCategory.name,
                    description: editFileList[0].response.mediaData.description || `Image for ${currentCategory.name}`,
                  } : removeExistingImage ? null : undefined,
                },
              }).then(() => {
                setEditPartnerCategoryId(undefined);
                setEditMode(false);
                setEditFileList([]);
                setRemoveExistingImage(false);
                setEditPartnerCategoryValues({ name: "", nameAr: "" });
                refetch();
              });
            }
          }}
          confirmLoading={updating}
          onCancel={() => {
            setEditMode(false);
            setEditPartnerCategoryId(undefined);
            setEditFileList([]);
            setRemoveExistingImage(false);
            setEditPartnerCategoryValues({ name: "", nameAr: "" });
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 8 }}>Image:</label>
            
            {/* Show current image from backend if exists and not being removed */}
            {(() => {
              const currentCategory = partnerCategoriesData?.getAllPartnerCategories.find(
                (rec) => rec.id === editPartnerCategoryId
              );
              const hasCurrentImage = currentCategory?.media && !removeExistingImage;
              const hasNewImage = editFileList.length > 0;
              
              return (
                <div>
                  {hasCurrentImage && !hasNewImage && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        <div style={{ width: 100, height: 100, border: "1px dashed #d9d9d9", borderRadius: 8, overflow: "hidden" }}>
                          <Image
                            src={currentCategory.media?.url}
                            alt={currentCategory.media?.altText || currentCategory.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            preview={{ mask: false }}
                          />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <span style={{ fontSize: 12, color: "#666" }}>Current Image</span>
                          <button
                            type="button"
                            onClick={() => {
                              setRemoveExistingImage(true);
                              messageApi.info("Image will be removed when you save changes");
                            }}
                            style={{
                              padding: "4px 8px",
                              border: "1px solid #ff4d4f",
                              borderRadius: 4,
                              background: "white",
                              color: "#ff4d4f",
                              cursor: "pointer",
                              fontSize: 12
                            }}
                          >
                            Remove Image
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {removeExistingImage && !hasNewImage && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ 
                        padding: 12, 
                        border: "1px solid #ff4d4f", 
                        borderRadius: 8, 
                        background: "#fff2f0", 
                        color: "#ff4d4f",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}>
                        <span>Image will be removed</span>
                        <button
                          type="button"
                          onClick={() => {
                            setRemoveExistingImage(false);
                            messageApi.info("Image removal cancelled");
                          }}
                          style={{
                            padding: "2px 6px",
                            border: "1px solid #ff4d4f",
                            borderRadius: 4,
                            background: "white",
                            color: "#ff4d4f",
                            cursor: "pointer",
                            fontSize: 11
                          }}
                        >
                          Undo
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Upload new image section */}
                  <Upload
                    maxCount={1}
                    listType="picture-card"
                    customRequest={handleCustomUpload}
                    onChange={({ fileList: newFileList }) => {
                      if (newFileList[0]?.status === "done") {
                        messageApi.success(`${newFileList[0].name} file uploaded successfully`);
                        setRemoveExistingImage(false); // Reset remove flag when new image is uploaded
                      } else if (newFileList[0]?.status === "error") {
                        messageApi.error(`${newFileList[0].name} file upload failed.`);
                      }
                      setEditFileList(newFileList);
                    }}
                    fileList={editFileList}
                    onRemove={async (file) => {
                      try {
                        // If the file has been uploaded and has an ID, delete it from the server
                        if (file.response?.id) {
                          await deleteMedia({
                            variables: {
                              id: file.response.id,
                            },
                          });
                          messageApi.success(`${file.name} deleted successfully`);
                        }
                      } catch (error) {
                        console.error("Delete error:", error);
                        messageApi.error(`Failed to delete ${file.name}`);
                      } finally {
                        // Always clear the edit file list regardless of delete success/failure
                        setEditFileList([]);
                      }
                    }}
                  >
                    {editFileList.length ? null : uploadButton}
                  </Upload>
                  
                  {hasNewImage && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "#52c41a" }}>
                      {hasCurrentImage ? "New image will replace the current one" : "New image will be added"}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8 }}>Name:</label>
            <Input
              value={editPartnerCategoryValues.name}
              onChange={(e) =>
                setEditPartnerCategoryValues((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8 }}>Name Ar:</label>
            <Input
              value={editPartnerCategoryValues.nameAr}
              onChange={(e) =>
                setEditPartnerCategoryValues((prev) => ({
                  ...prev,
                  nameAr: e.target.value,
                }))
              }
            />
          </div>
        </Modal>
        {contextHolder}
        <Col span={24}>
          <Table
            loading={loading}
            dataSource={dataSource}
            columns={[
              {
                title: "Name",
                dataIndex: "name",
                key: "name",
              },
              {
                title: "Name Ar",
                dataIndex: "nameAr",
                key: "nameAr",
              },
              {
                title: "Image",
                dataIndex: "image",
                key: "image",
              },
              { title: "Actions", dataIndex: "actions", key: "actions" },
            ]}
          />
        </Col>
      </Row>
    </RobinPage>
  );
}

export default PartnerCategoryList;
